import { I18nProvider } from '@lingui/react';
import { useQuery } from '@tanstack/react-query';
import { type JSX, type ReactNode, useEffect } from 'react';
import {
  type ActivatableLocale,
  activateLocale,
  bootstrapLocale,
  i18n,
  isDashboardLocale,
  PSEUDO_LOCALE,
  pseudoLocaleRequested,
} from '../lib/i18n.js';
import { meQuery } from '../lib/queries.js';

/** [i18n wave 1] Activates the founder's interface locale.
 *
 *  One effect, not two. It runs first with the browser's own language,
 *  then re-runs with the authoritative locale once /api/me answers — that
 *  value accounts for users.locale, which the browser cannot see.
 *
 *  This does NOT make the first paint match the browser's language. React
 *  commits before any effect runs, and activateLocale then awaits a dynamic
 *  import, so the first frame is always whatever bootstrapI18nSync() set.
 *  What the browser guess buys is a correct second frame for a founder
 *  whose /api/me call is slow — not a correct first one.
 *
 *  Deliberately does not skip when the target equals i18n.locale. That
 *  check looks like a harmless optimisation and is a bug: on mount
 *  i18n.locale is already the bootstrap value, so a founder whose stored
 *  locale is 'en' would short-circuit here while the in-flight browser
 *  guess ('es') resolved underneath and won. Re-activating the same locale
 *  is cheap — the dynamic import is cached.
 *
 *  Never blocks rendering on a catalog. A missing entry falls back to the
 *  source text, so a slow catalog costs a moment of English, not a blank
 *  screen. */
export function DashboardI18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const { data: me } = useQuery(meQuery());
  const serverLocale = isDashboardLocale(me?.locale) ? me.locale : null;

  useEffect(() => {
    // ?pseudo outranks everything, including an explicit users.locale —
    // it is a dev inspection mode, not a preference, and it is inert
    // outside a dev build.
    const target: ActivatableLocale = pseudoLocaleRequested()
      ? PSEUDO_LOCALE
      : (serverLocale ?? bootstrapLocale());
    let live = true;
    activateLocale(target, { shouldApply: () => live }).catch((err: unknown) => {
      // The UI stays on whatever catalog was active. Say so rather than
      // leaving "why is this page in English?" undiagnosable.
      console.error(`[i18n] failed to activate locale "${target}"`, err);
    });
    // Supersede an in-flight activation when a better answer arrives, so
    // ordering follows call order rather than whichever import resolved
    // first.
    return () => {
      live = false;
    };
  }, [serverLocale]);

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}
